import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Send, MessageCircle, ArrowLeft, Paperclip, Phone, Info } from "lucide-react";

export default function WhatsAppSimulator() {
  const [identity, setIdentity] = useState(() => {
    const raw = localStorage.getItem("os_sim_identity");
    return raw ? JSON.parse(raw) : null;
  });
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [body, setBody] = useState("");
  const [messages, setMessages] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantId, setTenantId] = useState("");
  const chatRef = useRef(null);

  useEffect(() => {
    api.get("/simulator/tenants").then((r) => { setTenants(r.data); if (r.data[0]) setTenantId(r.data[0].id); }).catch(() => {});
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(`os_sim_msgs_${identity?.phone}`);
    if (stored) setMessages(JSON.parse(stored));
  }, [identity]);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
    if (identity) {
      localStorage.setItem(`os_sim_msgs_${identity.phone}`, JSON.stringify(messages));
    }
  }, [messages, identity]);

  const start = () => {
    if (!name.trim() || !phone.trim()) return toast.error("Preencha nome e telefone");
    const id = { name: name.trim(), phone: phone.trim(), tenant_id: tenantId || undefined };
    setIdentity(id);
    localStorage.setItem("os_sim_identity", JSON.stringify(id));
  };

  const send = async (media) => {
    if (!body.trim() && !media) return;
    const text = body.trim();
    setBody("");
    const now = new Date().toISOString();
    setMessages((p) => [...p, { direction: "outbound", body: text || "📷 Foto", image: media?.dataUrl, at: now }]);
    try {
      const r = await api.post("/simulator/whatsapp", {
        customer_name: identity.name,
        customer_phone: identity.phone,
        body: text,
        tenant_id: identity.tenant_id || undefined,
        media_base64: media?.dataUrl,
        media_mimetype: media?.type,
        media_filename: media?.name,
      });
      const data = r.data || {};
      setMessages((p) => [...p, { direction: "inbound", body: data.reply || "", at: new Date().toISOString() }]);
    } catch (e) {
      toast.error("Erro ao enviar");
    }
  };

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => send({ dataUrl: reader.result, type: f.type || "image/jpeg", name: f.name });
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const reset = () => {
    localStorage.removeItem("os_sim_identity");
    localStorage.removeItem(`os_sim_msgs_${identity?.phone}`);
    setIdentity(null);
    setMessages([]);
  };

  if (!identity) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-950">
        <Card className="w-full max-w-md p-8 border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                Simulador WhatsApp
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">Abra uma OS como se fosse o cliente</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Seu nome</Label>
              <Input data-testid="sim-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="João da Silva" />
            </div>
            <div>
              <Label>Telefone (com DDI)</Label>
              <Input data-testid="sim-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+5511999999999" />
            </div>
            {tenants.length > 1 && (
              <div>
                <Label>Empresa (número do WhatsApp)</Label>
                <select data-testid="sim-tenant-select" className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                  {tenants.map((t) => <option key={t.id} value={t.id}>{t.nome_fantasia}</option>)}
                </select>
              </div>
            )}
            <Button onClick={start} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="sim-start-btn">
              Iniciar conversa
            </Button>
            <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-800 dark:text-blue-300">
                Envie "oi" para ver o menu: abrir chamado (fila → tipo → assunto → descrição → foto → confirmação), consultar chamados ou enviar informação sobre um chamado.
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-900 to-slate-800">
      <Card className="w-full max-w-md h-[85vh] flex flex-col overflow-hidden border-0 shadow-2xl">
        {/* Header */}
        <div className="px-4 py-3 bg-emerald-700 text-white flex items-center gap-3">
          <button onClick={reset} className="text-white/80 hover:text-white" data-testid="sim-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold">OS</div>
          <div className="flex-1">
            <p className="font-semibold">Atendimento OS Flow</p>
            <p className="text-xs text-emerald-100 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" /> online
            </p>
          </div>
          <Phone className="w-5 h-5 opacity-80" />
        </div>

        {/* Chat */}
        <div
          ref={chatRef}
          className="flex-1 overflow-y-auto p-4 space-y-2"
          style={{
            background: "linear-gradient(180deg, #DAD3C9 0%, #E8E0D7 100%)",
            backgroundImage: "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "30px 30px"
          }}
        >
          {messages.length === 0 && (
            <div className="text-center text-xs text-slate-500 bg-white/60 rounded-lg px-3 py-2 mx-auto max-w-xs">
              Envie sua primeira mensagem para abrir uma OS. Digite "status" a qualquer momento para consultar.
            </div>
          )}
          {messages.map((m, i) => {
            const out = m.direction === "outbound";
            return (
              <div key={i} className={`flex ${out ? "justify-end" : "justify-start"}`}>
                <div className={`${
                  out
                    ? "bg-[#DCF8C6] text-slate-900 rounded-2xl rounded-tr-sm"
                    : "bg-white text-slate-900 rounded-2xl rounded-tl-sm"
                  } px-3 py-2 shadow-sm max-w-[80%]`}
                  data-testid={`sim-msg-${i}`}
                >
                  {m.image && <img src={m.image} alt="foto" className="w-48 rounded-lg mb-1" />}
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  <p className="text-[10px] text-slate-500 text-right mt-0.5">
                    {new Date(m.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Input */}
        <div className="p-3 bg-slate-100 flex gap-2 items-end">
          <label className="mb-2 cursor-pointer text-slate-500 hover:text-emerald-600" title="Enviar foto">
            <Paperclip className="w-6 h-6" />
            <input type="file" accept="image/*" className="hidden" onChange={onPickPhoto} data-testid="sim-photo-input" />
          </label>
          <Input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Digite sua mensagem"
            data-testid="sim-input"
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
            className="bg-white border-slate-200"
          />
          <Button onClick={() => send()} className="bg-emerald-600 hover:bg-emerald-700 rounded-full h-10 w-10 p-0" data-testid="sim-send-btn">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
